"use client"
import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Home,
  Gift,
  ShoppingCart,
  LogOut,
  Menu,
  X,
  Search,
  Bell,
  Clock,
  BarChart3,
} from "lucide-react"
import logo from "../../assets/images/logo.png" // Import the logo
import { getPurchaseData, getPackageById } from "../../api/packageService" // Import purchase data function

interface UserDashboardLayoutProps {
  children: React.ReactNode
}

interface UserData {
  name: string;
  email: string;
  usertype: string;
  role?: string;
}

interface PurchaseData {
  purchaseId: string;
  createdAt: string;
  packageId: string | null;
  packageName?: string;
  packagePeriod?: string;
  expirationDate?: string;
  daysLeft?: number;
}

const UserDashboardLayout: React.FC<UserDashboardLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [purchases, setPurchases] = useState<PurchaseData[]>([])
  const location = useLocation()
  useEffect(() => {
  const storedUser = localStorage.getItem("userData");
  if (!storedUser) return;

  try {
    const parsedUser: UserData = JSON.parse(storedUser);
    setUserData(parsedUser);
  } catch (error) {
    console.error("Error parsing user data:", error);
    setUserData(null);
  }
}, []);

  
  // Function to parse period and calculate expiration date
  const calculateExpiration = (createdAt: string, period: string): { expirationDate: string; daysLeft: number } => {
    const createdDate = new Date(createdAt);
    let expirationDate = new Date(createdDate);
    
    // Handle invalid dates
    if (isNaN(createdDate.getTime())) {
      return {
        expirationDate: new Date().toISOString(),
        daysLeft: 0
      };
    }
    
    // Parse period like "3 months", "6 months", "1 year", etc.
    const periodParts = period.toLowerCase().split(' ');
    const periodValue = parseInt(periodParts[0]) || 0;
    const periodUnit = periodParts[1] || '';
    
    if (periodUnit.includes('month')) {
      expirationDate.setMonth(createdDate.getMonth() + periodValue);
    } else if (periodUnit.includes('year')) {
      expirationDate.setFullYear(createdDate.getFullYear() + periodValue);
    } else if (periodUnit.includes('day')) {
      expirationDate.setDate(createdDate.getDate() + periodValue);
    }
    
    const today = new Date();
    const timeDiff = expirationDate.getTime() - today.getTime();
    const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return {
      expirationDate: expirationDate.toISOString(),
      daysLeft: daysLeft // Keep negative values to properly filter them out
    };
  };
  
  // Function to refresh purchase data
  // const refreshPurchaseData = async () => {
  //   try {
  //     const storedUser = localStorage.getItem("userData");
  //     if (!storedUser) return;
      
  //     const userData = JSON.parse(storedUser);
  //     const userId = userData._id;
      
  //     // Fetch actual purchase data from API
  //     const response = await getPurchaseData(userId);
      
  //     if (response.status === 200 && response.data) {
  //       // Get package details to determine the period
  //       const purchasesWithDetails = await Promise.all(
  //         response.data.purchases.map(async (purchase: any) => {
  //           let packageName = "Individual Courses";
  //           let packagePeriod = "";
            
  //           // If this is a package purchase, get package details
  //           if (purchase.packageId) {
  //             try {
  //               const packageResponse = await getPackageById(purchase.packageId);
  //               if (packageResponse.status === 200 && packageResponse.data) {
  //                 packageName = packageResponse.data.title;
  //                 // Get the period from price details (assuming first price detail)
  //                 if (packageResponse.data.priceDetails && packageResponse.data.priceDetails.length > 0) {
  //                   packagePeriod = packageResponse.data.priceDetails[0].period;
  //                 }
  //               }
  //             } catch (error) {
  //               console.error("Error fetching package details:", error);
  //             }
  //           }
            
  //           return {
  //             purchaseId: purchase.purchaseId,
  //             createdAt: purchase.createdAt,
  //             packageId: purchase.packageId,
  //             packageName: packageName,
  //             packagePeriod: packagePeriod
  //           };
  //         })
  //       );
        
  //       // Calculate expiration dates for each purchase
  //       const purchasesWithExpiration = purchasesWithDetails
  //         .map(purchase => {
  //           if (purchase.packagePeriod) {
  //             const { expirationDate, daysLeft } = calculateExpiration(purchase.createdAt, purchase.packagePeriod);
  //             return {
  //               ...purchase,
  //               expirationDate,
  //               daysLeft
  //             };
  //           }
  //           return purchase;
  //         })
  //         .filter(p => {
  //           // Type guard to ensure daysLeft exists
  //           const purchaseWithDays = p as PurchaseData & { daysLeft: number };
  //           // Only show expiring soon (10 days or less, but not expired)
  //           return purchaseWithDays.daysLeft !== undefined && purchaseWithDays.daysLeft <= 10 && purchaseWithDays.daysLeft > 0;
  //         });
        
  //       setPurchases(purchasesWithExpiration);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching purchase data:", error);
  //   }
  // };
  
  const refreshPurchaseData = useCallback(async () => {
  try {
    const storedUser = localStorage.getItem("userData");
    if (!storedUser) return;

    const userData = JSON.parse(storedUser);
    const userId = userData._id;

    if (!userId) return;

    const response = await getPurchaseData(userId);

    if (response.status === 200 && response.data) {
      const purchasesWithDetails = await Promise.all(
        response.data.purchases.map(async (purchase: any) => {
          let packageName = "Individual Courses";
          let packagePeriod = "";

          if (purchase.packageId) {
            try {
              const packageResponse = await getPackageById(purchase.packageId);

              if (packageResponse.status === 200 && packageResponse.data) {
                packageName = packageResponse.data.title;

                if (
                  packageResponse.data.priceDetails &&
                  packageResponse.data.priceDetails.length > 0
                ) {
                  packagePeriod =
                    packageResponse.data.priceDetails[0].period;
                }
              }
            } catch (error) {
              console.error(
                "Error fetching package details:",
                error
              );
            }
          }

          return {
            purchaseId: purchase.purchaseId,
            createdAt: purchase.createdAt,
            packageId: purchase.packageId,
            packageName,
            packagePeriod,
          };
        })
      );

     const purchasesWithExpiration = purchasesWithDetails
      .map((purchase) => {
        if (purchase.packagePeriod) {
          const { expirationDate, daysLeft } =
            calculateExpiration(
              purchase.createdAt,
              purchase.packagePeriod
            );

          return {
            ...purchase,
            expirationDate,
            daysLeft,
          };
        }

        return {
          ...purchase,
          expirationDate: undefined,
          daysLeft: undefined,
        };
      })
      .filter(
        (p) =>
          p.daysLeft !== undefined &&
          p.daysLeft <= 10 &&
          p.daysLeft > 0
      );

          setPurchases(purchasesWithExpiration);
        }
      } catch (error) {
        console.error("Error fetching purchase data:", error);
      }
    }, []);

  // Fetch purchase data and calculate expiration dates
  // useEffect(() => {
  //   refreshPurchaseData();
  // }, [refreshPurchaseData]);
  
  // Refresh purchase data when user navigates to dashboard
  // useEffect(() => {
  //   if (location.pathname === '/user-dashboard') {
  //     refreshPurchaseData();
  //   }
  // }, [location.pathname,]);


useEffect(() => {
  if (location.pathname === "/user-dashboard") {
    refreshPurchaseData();
  }
}, [location.pathname, refreshPurchaseData]);


  const handleLogout = () => {
    console.log("Logging out user...")
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    localStorage.removeItem("userData")
    localStorage.removeItem("AUTH_TOKEN")
    localStorage.removeItem("loginHistoryId")
    window.location.href = "/login"
  }
  
  // Function to get initials from user's name
  const getUserInitials = (name?: string): string => {
  if (!name || typeof name !== "string") return "U";

  return name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
};

  
  const isPrivilegedUser = userData?.role === 'moderator' || userData?.role === 'support_user' || userData?.role === 'admin';

  const navigationItems = [
    { name: "DashBoard", path: "/user-dashboard", icon: Home },
    { name: "Offerings", path: "/user-offerings", icon: Gift },
    { name: "Purchased", path: "/UserPurchases", icon: ShoppingCart },
    { name: "Performance Report", path: "/user/learnrkb", icon: BarChart3 },
    ...(isPrivilegedUser ? [{ name: "Watch History", path: "/admin/watch-history", icon: Clock }] : []),
  ]
  
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}
      
      {/* Sidebar - Made more compact */}
      <div
        className={`
        fixed lg:static inset-y-0 left-0 z-50 w-48 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        <div className="flex flex-col h-full">
          {/* Header with Logo - Made more compact */}
          <div className="flex items-center justify-between p-2 border-b">
            <div className="flex items-center space-x-1.5">
              <Link to="/userDashboard" className="flex items-center">
                <img 
                  src={logo} 
                  alt="RKB Ventures Logo" 
                  className="h-10 w-auto" 
                />
              </Link>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation - Made more compact */}
          <nav className="flex-1 px-2 py-3 space-y-0.5">
            {/* Existing navigation items */}
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center space-x-1.5 px-2 py-1 rounded-md text-[18px] font-medium transition-colors
                    ${isActive ? "bg-purple-100 text-purple-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}             
            
            {/* Expiration Countdown Section - Made more compact */}
            <div className="mt-auto">
              {purchases.length > 0 && (
                <div className="pt-1.5 mt-1.5 border-t border-gray-200">
                  <h3 className="px-2 text-[9px] font-semibold text-gray-500 uppercase tracking-wider">Expiring Soon</h3>
                  <div className="mt-1 space-y-1">
                    {purchases.map((purchase) => (
                      <div key={purchase.purchaseId} className="px-2 py-1 bg-red-50 rounded-md">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] font-medium text-gray-900 truncate">
                              {purchase.packageName}
                            </p>
                            <div className="flex items-center mt-0.5">
                              <Clock className="flex-shrink-0 h-2 w-2 text-red-500 mr-0.5" />
                              <span className="text-[8px] font-medium text-red-700">
                                {purchase.daysLeft !== undefined ? (
                                  <>
                                    {purchase.daysLeft} {purchase.daysLeft === 1 ? 'day' : 'days'} left
                                  </>
                                ) : (
                                  'Calculating...'
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>
          
          {/* Logout - Made more compact */}
          <div className="p-2 border-t">
            <button
              onClick={() => setShowLogoutConfirmation(true)}
              className="flex items-center space-x-1.5 px-2 py-1 rounded-md text-[18px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 w-full transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
       
        {/* <header className="bg-white shadow-sm border-b px-2.5 py-2 lg:px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-1.5">
                <span className="font-semibold text-gray-900 text-sm hidden sm:block">Rakesh Bansal Ventures</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="hidden md:flex items-center space-x-1">
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <Search className="w-5 h-5" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600 relative">
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                </button>
              </div>
              
             
              <div className="flex items-center space-x-1">
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-[15px]">
                    {userData ? getUserInitials(userData.name) : "U"}
                  </span>
                </div>
                <button
                  onClick={() => setShowLogoutConfirmation(true)}
                  className="hidden md:flex items-center space-x-1 text-[11px] font-medium text-gray-700 hover:text-purple-600 transition-colors"
                >
                  <span className="hidden lg:inline-block">
                    {userData?.name ? userData.name.split(" ")[0] : "User"}
                    </span>
                  <LogOut className="w-5 h-5 ml-1" />
                </button>
                <button
                  onClick={() => setShowLogoutConfirmation(true)}
                  className="md:hidden text-gray-500 hover:text-red-500"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header> */}



 <header className="bg-white shadow-sm border-b px-3 py-3">
          <div className="flex items-center justify-center">
            {/* Nav icon cards only — no brand text on mobile */}
            <nav className="flex md:hidden items-center gap-2">
              {[
                { label: "DashBoard",  icon: Home,href: "/user-dashboard"  },
                { label: "Offerings",  icon: Gift,     href: "/user-offerings"  },
                { label: "Purchased",    icon: ShoppingCart, href: "/UserPurchases"    },
                { label: "Performance", icon: BarChart3, href: "/user/learnrkb" },
                ...(isPrivilegedUser ? [{ label: "Watch History", icon: Clock, href: "/admin/watch-history" }] : []),
              ].map(({ label, icon: Icon, href }) => (
                <Link
                  key={label}
                  to={href}
                  className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl bg-gray-100 hover:bg-purple-50 transition-colors min-w-[64px]"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shadow-sm">
                    <Icon className="w-6 h-6 text-purple-700" />
                  </div>
                  <span className="text-[11px] font-medium text-gray-700">{label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </header>


        
       
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
      
      {/* Logout Confirmation Modal - Made more compact */}
      {showLogoutConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg p-3 max-w-xs w-full shadow-xl">
            <h3 className="text-sm font-semibold text-gray-900 mb-1.5">Confirm Logout</h3>
            <p className="text-gray-600 mb-3 text-xs">Are you sure you want to logout? You'll need to sign in again to access your account.</p>
            <div className="flex justify-end space-x-1.5">
              <button
                onClick={() => setShowLogoutConfirmation(false)}
                className="px-2.5 py-1 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-2.5 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center shadow-sm hover:shadow-md text-xs"
              >
                <LogOut className="w-2.5 h-2.5 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDashboardLayout